const ApiService = require("../../../services/ApiService");
const { resolveVariationMapStrategy } = require("../../../helper/variationMapStrategy");

// Non-reactive per-itemId timing metrics for the variation-map A/B harness (kept out of Vuex state).
const variationLoadMetrics = {};

// Only emit the timing line in test mode (URL contains ?vmap=…) to keep the production console clean.
function isVariationMapDebug()
{
    return typeof window !== "undefined" && !!window.location && (/[?&]vmap=/).test(window.location.search || "");
}

function logVariationLoadMetrics(itemId)
{
    const metrics = variationLoadMetrics[itemId];

    delete variationLoadMetrics[itemId];

    if (!metrics || metrics.startedAt === null || typeof performance === "undefined" || !isVariationMapDebug())
    {
        return;
    }

    const totalMs = Math.round(performance.now() - metrics.startedAt);
    const trimMode = (typeof App !== "undefined" && App.config && App.config.item && App.config.item.variationMapTrimPayload) ? "trim" : "full";

    // eslint-disable-next-line no-console
    console.info(`[vmap] strategy=${resolveVariationMapStrategy()} payload=${trimMode} totalMs=${totalMs} requests=${metrics.requestCount} variations=${metrics.variationCount} itemId=${itemId}`);
}

const state = () => ({
    attributes: [],
    isVariationSelected: true,
    pendingAfterKey: null,
    pendingItemId: null,
    selectedAttributes: {},
    selectedUnit: null,
    units: [],
    variations: [],
    variationsLoading: false,
    variationsLoadStarted: false
});

const mutations =
    {
        setIsVariationSelected(state, isVariationSelected)
        {
            state.isVariationSelected = !!isVariationSelected;
        },

        setItemAttributes(state, attributes)
        {
            state.attributes = attributes;
        },

        setItemSelectedAttributes(state, selectedAttributes)
        {
            state.selectedAttributes = selectedAttributes;
        },

        selectItemAttribute(state, { attributeId, attributeValueId })
        {
            state.selectedAttributes[attributeId] = attributeValueId;
        },

        selectItemUnit(state, selectedUnit)
        {
            state.selectedUnit = selectedUnit;
        },

        setItemVariations(state, variations)
        {
            state.variations = variations;
        },

        addItemVariations(state, variations)
        {
            state.variations = state.variations || [];
            state.variations.push(...variations);

            state.units = state.units || [];
            for (const variation of variations)
            {
                state.units[variation.unitCombinationId] = variation.unitName;
            }
        },

        setVariationsLoading(state, loading)
        {
            state.variationsLoading = loading;
        },

        setPendingVariationLoad(state, { afterKey, itemId })
        {
            state.pendingAfterKey = afterKey || null;
            state.pendingItemId = itemId || null;
        },

        setVariationsLoadStarted(state, started)
        {
            state.variationsLoadStarted = !!started;
        },

        setUnits(state, units)
        {
            state.units = units;
        }
    };

const actions =
    {
        // eslint-disable-next-line complexity
        setVariationSelect({ commit, dispatch }, variationSelect)
        {
            const attributes         = variationSelect.attributes;
            const variations         = variationSelect.variations;
            const initialVariation   = variations.find(variation => variationSelect.initialVariationId === parseInt(variation.variationId));
            const initialUnit        = initialVariation && initialVariation.unitCombinationId || null;
            const selectedAttributes = {};
            const units              = {};

            for (const attribute of attributes)
            {
                let variationAttribute;

                if ((App.config.item.showPleaseSelect && variationSelect.isPleaseSelectOption) || !initialVariation)
                {
                    selectedAttributes[attribute.attributeId] = -1;
                }
                else
                {
                    variationAttribute = initialVariation.attributes.find(variationAttribute => variationAttribute.attributeId === attribute.attributeId);
                    selectedAttributes[attribute.attributeId] = variationAttribute ? variationAttribute.attributeValueId : null;
                }

            }

            for (const variation of variations)
            {
                units[variation.unitCombinationId] = variation.unitName;
            }

            if (variationSelect.afterKey)
            {
                commit("setPendingVariationLoad", {
                    itemId: variationSelect.itemId,
                    afterKey: variationSelect.afterKey
                });

                // Deep-link safety: a preselected variation NOT on the SSR first page can only be resolved
                // once the full map is loaded -> force the load regardless of the chosen strategy.
                if (variationSelect.initialVariationId && !initialVariation)
                {
                    dispatch("startVariationLoad");
                }
                else if (resolveVariationMapStrategy() === "eager")
                {
                    dispatch("startVariationLoad");
                }
            }

            commit("selectItemUnit", initialUnit);
            commit("setItemAttributes", attributes);
            commit("setItemSelectedAttributes", selectedAttributes);
            commit("setItemVariations", variations);
            commit("setUnits", units);
        },

        // Idempotent kickoff for the recursive variation-map load. Safe to call from multiple triggers
        // (idle timer + user interaction); the variationsLoadStarted guard makes the first one win.
        startVariationLoad({ commit, dispatch, state })
        {
            if (state.variationsLoadStarted || !state.pendingAfterKey)
            {
                return;
            }

            commit("setVariationsLoadStarted", true);

            variationLoadMetrics[state.pendingItemId] = {
                startedAt: (typeof performance !== "undefined" && performance.now) ? performance.now() : null,
                requestCount: 0,
                variationCount: 0
            };

            dispatch("fetchVariations", {
                itemId: state.pendingItemId,
                afterKey: state.pendingAfterKey
            });
        },

        fetchVariations({ commit, dispatch }, { afterKey, itemId })
        {
            return new Promise((resolve, reject) =>
            {
                commit("setVariationsLoading", true);
                ApiService
                    .get("/rest/io/variations/map", {
                        itemId: itemId,
                        afterKey: afterKey
                    })
                    .done(response =>
                    {
                        const metrics = variationLoadMetrics[itemId];

                        if (metrics)
                        {
                            metrics.requestCount += 1;
                        }

                        if (response.variations && response.variations.length)
                        {
                            if (metrics)
                            {
                                metrics.variationCount += response.variations.length;
                            }
                            commit("addItemVariations", response.variations);
                        }
                        if (response.afterKey)
                        {
                            dispatch("fetchVariations", {
                                itemId: itemId,
                                afterKey: response.afterKey
                            });
                        }
                        else
                        {
                            commit("setVariationsLoading", false);
                            logVariationLoadMetrics(itemId);
                        }

                        resolve();
                    })
                    .fail(error =>
                    {
                        commit("setVariationsLoading", false);
                        reject(error);
                    });
            });
        }
    };

const getters =
    {
    };

export default
{
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
